import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';

import { API_URL } from '@constants';
import type {
  AcceptConditionsResponse,
  Geocode,
  Group,
  NotificationSettings,
  Room,
  SystemToken,
  User,
  UserSettings,
} from '@models';
import { ModalService } from './modal.service';

@Service()
export class UserService {
  http = inject(HttpClient);
  modalService = inject(ModalService);

  getUserSettings = () =>
    this.http.get<UserSettings>(`${API_URL}/user/settings`);

  updateUserPreferences = (body: Record<string, unknown>) =>
    this.http.post<void>(`${API_URL}/user/preferences`, body);

  getUserAlive = () => this.http.get<void>(`${API_URL}/user/alive`);

  checkUsernameExists = (formData: FormData) =>
    this.http.post<{ exists: boolean }>(
      `${API_URL}/users/existsusername`,
      formData
    );

  checkFullNameExists = (formData: FormData) =>
    this.http.post<{ exists: boolean }>(
      `${API_URL}/users/existsfullname`,
      formData
    );

  getUserConfigForGuest = (userRid: number) =>
    this.http.get<Record<string, unknown>>(
      `${API_URL}/users/${userRid}/config?guest=true`
    );

  updateUser = (userId: number, body: Record<string, unknown>) =>
    this.http.put<User>(`${API_URL}/users/${userId}`, body);

  getResourceDetails = <T = unknown>(resourceType: string, id: number) =>
    this.http.get<T>(`${API_URL}/${resourceType}/${id}`);

  getAvailableUsers = () =>
    this.http.get<{ available: number; max: number; internal?: number }>(
      `${API_URL}/users/availableusers`
    );

  getGroupsList = (params?: Record<string, string | number>) =>
    this.http.get<Group[]>(`${API_URL}/groups`, { params });

  getUsersList = (params?: Record<string, string | number>) =>
    this.http.get<User[]>(`${API_URL}/users`, { params });

  getUserConfig = <T = Record<string, unknown>>(
    userRid: number,
    isGuest = false
  ) => this.http.get<T>(`${API_URL}/users/${userRid}/config?guest=${isGuest}`);

  getUser = (userRid: number) =>
    this.http.get<User>(`${API_URL}/users/${userRid}`);

  getUserWithParams = (
    userRid: number,
    params?: Record<string, string | number | boolean>
  ) => this.http.get<User>(`${API_URL}/users/${userRid}`, { params });

  exportUsers = (selectedIds: string) =>
    this.http.post(`${API_URL}/users/export`, selectedIds, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      responseType: 'blob',
    });

  getGroup = (groupRid: number) =>
    this.http.get<Group>(`${API_URL}/groups/${groupRid}`);

  resetOtp = (userRid: number, updateDate: number) =>
    this.http.put<{ updateDate: number; otpSharedKey: boolean }>(
      `${API_URL}/users/${userRid}/resetotp?updateDate=${updateDate}`,
      {}
    );

  sendLoginInfo = (userRid: number, sendType: number) =>
    this.http.post<void>(
      `${API_URL}/users/${userRid}/logininfo?sendType=${sendType}`,
      {}
    );

  generatePassword = () =>
    this.http.get<{ password: string }>(`${API_URL}/user/password-generate`);

  updateGroup = (groupId: number, body: Record<string, unknown>) =>
    this.http.put<Group>(`${API_URL}/groups/${groupId}`, body);

  changePassword = (body: Record<string, unknown>) =>
    this.http.post<void>(`${API_URL}/user/changepassword`, body);

  forceChangePassword = (body: Record<string, unknown>) =>
    this.http.post<UserSettings>(`${API_URL}/user/forcechangepassword`, body);

  importUsers = (formData: FormData) =>
    this.http.post<{ imported: number; failed: number }>(
      `${API_URL}/users/import`,
      formData,
      {
        reportUploadProgress: true,
        observe: 'events',
      }
    );

  getUsers = () => this.http.get<User[]>(`${API_URL}/users`);

  createUser = (body: Record<string, unknown>) =>
    this.http.post<User>(`${API_URL}/users`, body);

  updateUserNotificationSettings = (
    userRid: number,
    body: Record<string, unknown>
  ) =>
    this.http.put<NotificationSettings>(
      `${API_URL}/users/${userRid}/notificationsettings`,
      body
    );

  getUserNotificationSettings = (userRid: number) =>
    this.http.get<NotificationSettings>(
      `${API_URL}/users/${userRid}/notificationsettings`
    );

  getGeocode = (address: string) =>
    this.http.get<{ results: Geocode[] }>('/agora-api/geocode', {
      params: { address },
    });

  enableUser = (userRid: number, enable: boolean, updateDate?: number) =>
    this.http.put<void>(
      `${API_URL}/users/${userRid}/enable?enable=${enable}${
        updateDate ? `&updateDate=${updateDate}` : ''
      }`,
      {}
    );

  getUserSignatureImage = (userRid: number) =>
    this.http.get(`${API_URL}/users/${userRid}/signatureimage?base64=true`, {
      responseType: 'text',
    });

  updateUserSignature = (userRid: number, body: Record<string, unknown>) =>
    this.http.put<User>(`${API_URL}/users/${userRid}`, body);

  getUserSystemToken = () =>
    this.http.get<SystemToken>(`${API_URL}/user/systemtoken`);

  deleteUserSystemToken = () => this.http.delete(`${API_URL}/user/systemtoken`);

  createUserSystemToken = (tokenData: Record<string, unknown>) =>
    this.http.post<SystemToken>(`${API_URL}/user/systemtoken`, tokenData);

  updateUserSystemToken = (tokenData: Record<string, unknown>) =>
    this.http.put<SystemToken>(`${API_URL}/user/systemtoken`, tokenData);

  getUserRooms = (userRid: number) =>
    this.http.get<Room[]>(`${API_URL}/users/${userRid}/listrooms`);

  acceptConditions = () =>
    this.http.post<AcceptConditionsResponse>(
      `${API_URL}/user/acceptconditions`,
      null
    );

  sendSupportMessage = (text: string) =>
    this.http.post<void>(`${API_URL}/user/support`, { text });

  getUserActivity = (
    resourceType: string,
    userRid: number,
    limit: number,
    offset: number
  ) =>
    this.http.get<{ action: number; timestamp: number; hasDetails: boolean }[]>(
      `${API_URL}/${resourceType}/${userRid}/activity`,
      {
        params: { limit, offset },
      }
    );

  getUserPlaceholder = (userRid: number, base64 = true, active = true) =>
    this.http.get(
      `${API_URL}/users/${userRid}/placeholder?base64=${base64}&active=${active}`,
      { responseType: 'text' }
    );

  getPlaceholderByName = (name: string, base64 = true) =>
    this.http.get(
      `${API_URL}/users/placeholder?base64=${base64}&name=${encodeURIComponent(
        name
      )}`,
      { responseType: 'text' }
    );

  deleteUserMemberships = (
    item: Nullable<{
      displayName?: string;
      rid?: number;
      permission?: unknown;
    }>,
    data: Record<string, unknown>,
    userRid: number
  ) =>
    this.modalService.openConfirmation({
      title: 'WEB_DIALOGS_GROUP_MEMBERSHIPS_DELETE',
      text: 'WEB_DIALOGS_CONFIRM',
      request: { url: `users/${userRid}`, body: data, method: 'put' },
      successText: 'WEB_NOTIFY_USER_DELETE_MEMBERSHIPS_1_SUCCESS',
      infoGroupName: item?.displayName,
      danger: true,
    });
}
